import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { observer } from 'mobx-react';
import { deepObserve } from 'mobx-utils';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '@lib/api';
import useAxios from 'axios-hooks';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Layout, Typography, Button, Table, Tooltip, Modal, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';
import { ReactComponent as IconEditorEdit } from '@svg/icon-editor-edit.svg';
import { ReactComponent as IconEditorChapter } from '@svg/icon-editor-chapter.svg';
import { ReactComponent as IconPublishing } from '@svg/icon-published-public.svg';
import { ReactComponent as IconEditorCoord } from '@svg/icon-editor-coord.svg';
import { ReactComponent as IconDirectLink } from '@svg/icon-editor-direct-link.svg';
import { ReactComponent as IconUpload } from '@svg/icon-upload.svg';
import { IVideoList } from '@interfaces/video.interface';
import { APIGetChannelVideos } from '@interfaces/api.interface';
import { UploaderContext, VideoState } from '@lib/video-uploader';
import useStores from '@lib/hooks/useStores';
import { IUserStore, ProcessingVideoData } from '@stores/userStore';
import { scMixinScreenReaderOnly } from '@utils/emotion/mixins';

import { TableRowSelection } from 'antd/lib/table/interface';
import Title from 'antd/lib/typography/Title';
import { formatDigitToHHmmss, getViewerUrlByStage } from '~/utils';
import { useAuthStore } from '@stores/auth';

const { Content } = Layout;
const { Text } = Typography;

type UploadingVideos = Record<
  string,
  {
    videoId: string | null;
    state: VideoState;
    progress: number;
  }
>;

type ProcessingVideos = Record<string, ProcessingVideoData>;

const Videos: React.FC = observer(() => {
  const { t } = useTranslation();
  const { userData } = useAuthStore();
  const { userStore } = useStores<{ userStore: IUserStore }>();
  const navigate = useNavigate();
  const location = useLocation();
  const refetchTimeoutHandler = useRef<number>(0);
  const [pageOffset, setPageOffset] = useState(0);
  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const controller = new AbortController();
  const [{ data, loading, error }, refetchVideos] = useAxios<APIGetChannelVideos>(
    {
      url: `/channel/${userData?.mainChannel}/video`,
      params: { offset: pageOffset, opt_fields: 'unpublished' },
      signal: controller.signal,
    },
    { useCache: false },
  );
  const videoUploader = useContext(UploaderContext);
  const [uploadingVideos, setUploadingVideos] = useState<UploadingVideos>({});
  const [processingVideos, setProcessingVideos] = useState<ProcessingVideos>({});

  const getUploadingVideosByVideoId = useCallback(
    (videoId: string) => {
      const key = Object.keys(uploadingVideos).find(
        (uid) => uploadingVideos[uid]?.videoId === videoId,
      );

      return key ? uploadingVideos[key] : null;
    },
    [uploadingVideos],
  );

  useEffect(() => {
    const initialUploadingVideos: UploadingVideos = {};
    Object.values(userStore.currentlyUploadingVideoDatas).forEach((v) => {
      Object.assign(initialUploadingVideos, {
        [v.uid]: {
          videoId: v.videoId,
          state: v.state,
          progress: v.progress,
        },
      });
    });

    setUploadingVideos(initialUploadingVideos);
    setProcessingVideos(userStore.currentlyProcessingVideoDatas);

    const uploadigVideoDeepObserveDisposer = deepObserve(
      userStore.currentlyUploadingVideoDatas,
      (change) => {
        if (change.observableKind !== 'object') return;

        switch (change.type) {
          case 'add':
            setUploadingVideos((prevState) => {
              const uid = change.name.toString();
              const newState = { ...prevState };
              newState[uid] = {
                videoId: null,
                state: 'presigning',
                progress: 0,
              };

              return newState;
            });
            clearTimeout(refetchTimeoutHandler.current);
            refetchTimeoutHandler.current = window.setTimeout(() => {
              refetchVideos();
            }, 1000);
            break;
          case 'remove':
            clearTimeout(refetchTimeoutHandler.current);
            refetchTimeoutHandler.current = window.setTimeout(() => {
              refetchVideos();
            }, 1000);
            break;
          case 'update':
            const uid = change.object.uid;

            switch (change.name) {
              case 'videoId':
                setUploadingVideos((prevState) => ({
                  ...prevState,
                  [uid]: {
                    ...prevState[uid],
                    videoId: change.newValue,
                  },
                }));
                break;
              case 'state':
                setUploadingVideos((prevState) => ({
                  ...prevState,
                  [uid]: {
                    ...prevState[uid],
                    state: change.newValue,
                  },
                }));
                break;
              case 'progress':
                setUploadingVideos((prevState) => ({
                  ...prevState,
                  [uid]: {
                    ...prevState[uid],
                    progress: change.newValue,
                  },
                }));
            }
        }
      },
    );

    const processingVideoDeepObserveDisposer = deepObserve(
      userStore.currentlyProcessingVideoDatas,
      (change) => {
        if (change.observableKind !== 'object') return;

        switch (change.type) {
          case 'add':
            setProcessingVideos((prevState) => {
              const videoId = change.name.toString();
              const newState = { ...prevState };

              newState[videoId] = change.object[videoId];

              return newState;
            });
            break;
          case 'remove':
            setProcessingVideos((prevState) => {
              const videoId = change.name.toString();
              const newState = { ...prevState };

              delete newState[videoId];

              return newState;
            });
            break;
          case 'update':
            setProcessingVideos((prevState) => {
              const videoId = change.object.videoId;
              const newState = { ...prevState };

              newState[videoId] = change.object;

              return newState;
            });
        }
      },
    );

    return () => {
      controller.abort();
      uploadigVideoDeepObserveDisposer();
      processingVideoDeepObserveDisposer();
      clearTimeout(refetchTimeoutHandler.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && error) {
      Modal.error({
        title: t('error.api.common.retrieve.server.title'),
        content: t('error.api.edit.failed'),
        okText: t('a11y.ok'),
      });
    }
  }, [loading, error, t]);

  useEffect(() => {
    refetchVideos();
  }, [location.key, refetchVideos]);

  const deleteSelectedVideos = () => {
    Modal.confirm({
      title: t('videos.action.delete.multiple.title', { quantity: selectedVideoIds.length }),
      icon: <ExclamationCircleOutlined />,
      content: t('videos.action.delete.multiple.description'),
      maskClosable: false,
      okText: t('common.delete'),
      okType: 'danger',
      cancelText: t('a11y.cancel'),
      async onOk() {
        for (const id of selectedVideoIds) {
          const uid = videoUploader.getUidByVideoId(id);
          if (uid) videoUploader.cancelVideoUpload(uid, true);
          await api.delete(`/video/${id}`);
        }

        setSelectedVideoIds([]);
        refetchVideos();
        message.success(
          t('videos.action.message.multiple.deleted', { quantity: selectedVideoIds.length }),
          4,
        );
      },
    });
  };

  const getVideoProcessingProgress = (videoData: ProcessingVideoData) => {
    const { chapters, progress: chapterProgress, currentProcessingChapterIdx } = videoData;

    let totalDuration = 0;
    let progress = 0;

    chapters.forEach((chapter) => (totalDuration += chapter.endTime - chapter.startTime));
    chapters.forEach((chapter) => {
      if (chapter.chapterIdx === currentProcessingChapterIdx) {
        // current chapter
        progress += Math.ceil(
          (((chapter.endTime - chapter.startTime) * (chapterProgress / 100)) / totalDuration) * 100,
        );
      } else if (chapter.chapterIdx < currentProcessingChapterIdx) {
        // previous chapters
        progress += Math.ceil(((chapter.endTime - chapter.startTime * 1) / totalDuration) * 100);
      }
    });

    return progress;
  };

  const columns: ColumnProps<IVideoList>[] = useMemo(
    () => [
      {
        title: t('video.common.columns.video'),
        dataIndex: 'videoId',
        key: 'videoId',
        className: 'td-video',
        render: function videoMetaComp(videoId, record) {
          const uploadingData = getUploadingVideosByVideoId(videoId);
          const videoUploaderUid = videoUploader.getUidByVideoId(videoId);
          const processingData = processingVideos[videoId];
          const hasThumbnail = !!record.thumbnails.length;
          const isUploading =
            record.jobs.transcoding === 'pending' && record.jobs.poseEstimation === 'pending';
          const isProcessing = record.jobs.poseEstimation === 'processing';

          return (
            <Link to={`/video/${videoId}/edit`} className="video-anchor">
              <div className="thumbnail-area">
                <div className="thumbnail-img">
                  <img
                    src={
                      hasThumbnail ? record.thumbnails[0].url : '/assets/player/default-video.png'
                    }
                    alt={record.title}
                  />
                </div>
                {!!record.duration && (
                  <div className="duration-box">
                    <span className="duration-value">
                      {formatDigitToHHmmss(record.duration, true, record.duration >= 3600, true)}
                    </span>
                  </div>
                )}
              </div>
              <div className="info">
                <strong className="title">{record.title}</strong>
                {isUploading && (
                  <div className="status-area">
                    <div className="job-progress">
                      <i className="icon icon--upload">
                        <IconUpload />
                      </i>
                      <span className="text">
                        {uploadingData
                          ? uploadingData.progress
                            ? uploadingData.progress < 100
                              ? t('videos.progress.uploading', {
                                  progress: uploadingData.progress ?? 0,
                                })
                              : t('videos.progress.uploading.complete')
                            : t('videos.progress.uploading.awaiting')
                          : t(
                              videoUploaderUid
                                ? 'videos.progress.uploading.awaiting'
                                : 'videos.progress.uploading.failed',
                            )}
                      </span>
                    </div>
                  </div>
                )}
                {isProcessing && (
                  <div className="status-area">
                    <div className="job-progress">
                      <i className="icon icon--chapter">
                        <IconEditorChapter width="20" height="20" />
                      </i>
                      <span className="text">
                        {processingData
                          ? processingData.progress
                            ? processingData.progress < 100
                              ? t('videos.progress.processing', {
                                  progress: getVideoProcessingProgress(processingData),
                                })
                              : t('videos.progress.processing.complete')
                            : t('videos.progress.processing.awaiting')
                          : t('videos.progress.processing.awaiting')}
                      </span>
                    </div>
                  </div>
                )}
                <div className="action-area">
                  <Tooltip placement="bottom" title={t('videos.action.edit.detail')}>
                    <div
                      role="button"
                      tabIndex={0}
                      className="action-item"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/video/${videoId}/edit`);
                      }}
                    >
                      <i className="icon">
                        <IconEditorEdit />
                      </i>
                      <span className="text">{t('videos.action.edit.detail')}</span>
                    </div>
                  </Tooltip>
                  {record.categories[0] !== undefined &&
                    record.jobs.transcoding === 'complete' &&
                    record.type === 'pose' && (
                      <Tooltip placement="bottom" title={t('videos.action.edit.chapter')}>
                        <div
                          role="button"
                          tabIndex={0}
                          className="action-item"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/video/${videoId}/editor/chapter`);
                          }}
                        >
                          <i className="icon">
                            <IconEditorChapter width="24" height="24" />
                          </i>
                          <span className="text">{t('videos.action.edit.chapter')}</span>
                        </div>
                      </Tooltip>
                    )}
                  {record.categories[0] !== undefined &&
                    record.jobs.transcoding === 'complete' &&
                    record.jobs.poseEstimation !== 'processing' && (
                      <Tooltip placement="bottom" title={t('videos.action.edit.publishing')}>
                        <div
                          role="button"
                          tabIndex={0}
                          className="action-item"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/video/${videoId}/visibility`);
                          }}
                        >
                          <i className="icon">
                            <IconPublishing width="24" height="24" />
                          </i>
                          <span className="text">{t('videos.action.edit.publishing')}</span>
                        </div>
                      </Tooltip>
                    )}
                  {record.categories[0] !== undefined &&
                    record.jobs.poseEstimation === 'complete' &&
                    record.type === 'pose' && (
                      // <Tooltip placement="bottom" title={t('tooltip.editor.coords.not.ready')}>
                      <Tooltip placement="bottom" title={t('videos.action.edit.coords')}>
                        <div
                          role="button"
                          tabIndex={0}
                          className="action-item"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/video/${videoId}/editor/coords`);
                          }}
                        >
                          <i className="icon">
                            <IconEditorCoord width="20" height="20" />
                          </i>
                          <span className="text">{t('videos.action.edit.coords')}</span>
                        </div>
                      </Tooltip>
                    )}
                  {record.jobs.transcoding === 'complete' && record.published && (
                    <Tooltip placement="bottom" title={t('videos.action.directlink')}>
                      <div
                        role="button"
                        tabIndex={0}
                        className="action-item"
                        onClick={(e) => {
                          e.preventDefault();

                          window.open(
                            `${getViewerUrlByStage()}/watch?v=${record.videoId}`,
                            '_blank',
                          );
                        }}
                      >
                        <i className="icon">
                          <IconDirectLink width="24" height="24" />
                        </i>
                        <span className="text">{t('videos.action.edit.coords')}</span>
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            </Link>
          );
        },
      },
      // {
      //   title: t('video.common.columns.duration'),
      //   dataIndex: 'duration',
      //   key: 'duration',
      //   className: 'td-time',
      //   render: duration => (0)
      // },
      {
        title: t('video.common.columns.published'),
        dataIndex: 'published',
        key: 'published',
        className: 'td-published',
        width: 160,
        align: 'center',
        render: (published) => t(`video.common.published.${published}`),
      },
      {
        title: t('video.common.columns.date'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        className: 'td-date',
        width: 200,
        align: 'center',
        render: function videoCreatedAtComp(date, record) {
          const videoUploadingUid = videoUploader.getUidByVideoId(record.videoId);

          return (
            <div className="video-date">
              <div className="date">
                {dayjs(
                  new Date(
                    record.published
                      ? record.lastPublishedAt ?? record.createdAt
                      : record.createdAt,
                  ),
                ).format('YYYY. MM. DD')}
              </div>
              <div className="status">
                {record.jobs.transcoding === 'pending' && record.jobs.poseEstimation === 'pending'
                  ? videoUploadingUid
                    ? t('videos.status.uploading')
                    : ''
                  : record.jobs.transcoding !== 'complete'
                  ? t('videos.status.transcoding')
                  : record.published
                  ? t('videos.status.published.true')
                  : record.jobs.poseEstimation !== 'pending'
                  ? t(`videos.status.estimation.${record.jobs.poseEstimation}`)
                  : t('videos.status.published.false')}
              </div>
            </div>
          );
        },
      },
      {
        title: t('video.common.columns.views'),
        dataIndex: 'view',
        key: 'view',
        className: 'td-views',
        width: 200,
        align: 'center',
        render: (view) => Number(view).toLocaleString(),
      },
      // {
      //   title: t('video.common.columns.comments'),
      //   dataIndex: 'commentCount',
      //   key: 'commentCount',
      //   className: 'td-comment',
      //   align: 'right',
      //   render: count => Number(count).toLocaleString()
      // },
      // {
      //   title: t('video.common.columns.like'),
      //   dataIndex: 'like',
      //   key: 'like',
      //   className: 'td-like',
      //   align: 'right',
      //   render: count => Number(count).toLocaleString()
      // },
      // {
      //   title: t('video.common.columns.dislike'),
      //   dataIndex: 'dislike',
      //   key: 'dislike',
      //   className: 'td-dislike',
      //   align: 'right',
      //   render: count => Number(count).toLocaleString()
      // }
    ],
    [getUploadingVideosByVideoId, processingVideos, navigate, t, videoUploader],
  );

  const rowSelectionHandler: TableRowSelection<any> = {
    selectedRowKeys: selectedVideoIds,
    onChange: (selectedRowKeys: any) => {
      setSelectedVideoIds(selectedRowKeys);
    },
    // getCheckboxProps: (record: any) => ({
    //   disabled: record.name === 'Disabled User', // Column configuration not to be checked
    //   name: record.name,
    // }),
  };

  const pagingHandler = (page: number) => {
    page === 1 ? setPageOffset(0) : setPageOffset((page - 1) * 10);
  };

  return (
    <ContentBlock>
      <div className="content-header">
        <Typography>
          <Title level={2}>{t('videos.title')}</Title>
        </Typography>
        <div className="header-action-area">
          {selectedVideoIds.length > 0 ? (
            <Text className="selected-text">
              {t('videos.action.selected.length', { quantity: selectedVideoIds.length })}
            </Text>
          ) : null}
          <Button
            type="primary"
            danger
            disabled={!selectedVideoIds.length}
            onClick={() => deleteSelectedVideos()}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
      <VideoListBlock>
        <Table
          rowSelection={rowSelectionHandler}
          dataSource={data?.data}
          columns={columns}
          loading={loading}
          rowKey={(record) => record.videoId}
          rowClassName={({ jobs }) =>
            (jobs.transcoding === 'pending' && jobs.poseEstimation === 'pending') ||
            jobs.poseEstimation === 'processing'
              ? 'has-jobs'
              : ''
          }
          onRow={() => ({
            onMouseEnter: (e) => {
              e.currentTarget.classList.add('is-hover');
            },
            onMouseLeave: (e) => {
              e.currentTarget.classList.remove('is-hover');
            },
          })}
          pagination={{
            total: data?._meta.total,
            pageSize: 10,
            onChange: pagingHandler,
          }}
        />
      </VideoListBlock>
    </ContentBlock>
  );
});

const ContentBlock = styled(Content)`
  padding: 19px 36px;

  .content-header {
    display: flex;
    justify-content: space-between;
  }

  h2.ant-typography {
    margin-bottom: 0 !important;
  }

  .header-action-area {
    margin-bottom: 16px;
    display: flex;
    align-items: flex-end;

    .selected-text {
      margin: 0 16px 0 0;
      line-height: 32px;
      color: ${({ theme }) => theme.colors.fontSubColor};
    }
  }
`;

interface StyledVideoListBlockProps {
  newVideoId?: string | null;
}

const VideoListBlock = styled.div<StyledVideoListBlockProps>`
  display: block;
  width: 100%;

  /* ${({ newVideoId }) => {
    return css`
      [data-row-key='${newVideoId}'] {
        td {
          background-color: #f4e6ff !important;
        }

        &:hover td {
          background-color: #f0dcff !important;
        }
      }
    `;
  }} */

  tr.ant-table-row.has-jobs {
    .status-area {
      display: block;
    }

    .action-area {
      display: none;
    }

    &.is-hover {
      .status-area {
        display: none;
      }

      .action-area {
        display: block;
      }
    }
  }

  .video-anchor {
    display: flex;
    align-items: center;
  }

  .thumbnail-area {
    flex: 0 0 120px;
    overflow: hidden;
    position: relative;
  }

  .thumbnail-img {
    position: relative;
    z-index: 0;

    img {
      display: block;
      width: 120px;
      height: auto;
    }
  }

  .duration-box {
    position: absolute;
    right: 3px;
    bottom: 3px;
  }

  .duration-value {
    padding: 0 4px 1px;
    border-radius: 3px;
    background: rgba(9, 9, 9, 0.8);
    font-size: 12px;
    line-height: 1.3;
    color: #f5f6f8;
  }

  .info {
    flex: 1 1 auto;
    overflow: hidden;
    padding: 4px 24px;
  }

  .title {
    display: inline-block;
    max-width: 380px;
    -webkit-line-clamp: 2;
    text-overflow: ellipsis;
    font-weight: normal;
  }

  .action-area {
    height: 24px;
    margin: 24px 12px 12px 0;

    .action-item {
      display: inline-block;
      vertical-align: middle;
      margin-left: 18px;

      &:first-of-type {
        margin-left: 0;
      }
    }

    .icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 24px;
      height: 24px;
    }

    .text {
      ${scMixinScreenReaderOnly};
    }
  }

  .status-area {
    display: none;
    margin: 24px 0 12px;
    color: #334d6e;

    .icon {
      display: inline-block;
      margin-right: 6px;
      line-height: 0;
      vertical-align: top;

      &.icon--upload {
        padding: 4px;
      }

      &.icon--chapter {
        padding: 2px;
      }

      svg {
        vertical-align: top;

        path {
          fill: #334d6e;
        }
      }
    }
  }

  th.td-video {
    padding-left: 158px;
  }

  .ant-pagination.ant-table-pagination {
    .ant-pagination-item-active {
      background: #5100ce;

      a {
        color: #fff;
      }
    }
  }
`;

export default Videos;
